import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/axios';
import type {
  AiSession, AiSessionEndpointSummary, AiSessionFilters, CursorPage, DpiEvent, PaginatedResponse,
} from '@/types/api';
import type { WorkflowGraph } from '@/types/workflow';

const ACTIVITY_PAGE_SIZE = 50;

// ── Query key factory ──────────────────────────────────────────────────────
export const aiSessionKeys = {
  all: ['ai-sessions'] as const,
  list: (filters: AiSessionFilters) => [...aiSessionKeys.all, 'list', filters] as const,
  detail: (id: string) => [...aiSessionKeys.all, 'detail', id] as const,
  endpointSummary: (filters: EndpointSummaryFilters) => [...aiSessionKeys.all, 'endpoint-summary', filters] as const,
};

export interface EndpointSummaryFilters {
  vendor?: string;
  status?: string;
  search?: string;
  endpointId?: string;
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
}

/** Grand totals across every endpoint matching the filter — independent of
 *  the current page, for KPI cards that shouldn't collapse to a page subtotal. */
export interface EndpointSummaryTotals {
  sessionCount: number;
  tokenCount: number;
  activeCount: number;
  detectionCount: number;
  alertCount: number;
}

export interface EndpointSummaryResult extends PaginatedResponse<AiSessionEndpointSummary> {
  totals: EndpointSummaryTotals;
}

// ── Hooks ─────────────────────────────────────────────────────────────────

export function useAiSessions(filters: AiSessionFilters = {}) {
  return useQuery({
    queryKey: aiSessionKeys.list(filters),
    queryFn: async () => {
      const { data } = await apiClient.get<PaginatedResponse<AiSession>>(
        '/ai-sessions',
        { params: filters },
      );
      return data;
    },
  });
}

export function useAiSessionEndpointSummary(filters: EndpointSummaryFilters = {}) {
  return useQuery({
    queryKey: aiSessionKeys.endpointSummary(filters),
    queryFn: async () => {
      const { data } = await apiClient.get<EndpointSummaryResult>(
        '/ai-sessions/endpoint-summary',
        { params: filters },
      );
      return data;
    },
  });
}

export function useAiSession(id: string) {
  return useQuery({
    queryKey: aiSessionKeys.detail(id),
    queryFn: async () => {
      const { data } = await apiClient.get<AiSession>(`/ai-sessions/${id}`);
      return data;
    },
    enabled: Boolean(id),
  });
}

export interface SessionEvent {
  id: string;
  type: string;
  timestamp: string;
  description: string;
}

export function useSessionEvents(id: string) {
  return useQuery({
    queryKey: [...aiSessionKeys.detail(id), 'events'] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<SessionEvent[]>(`/ai-sessions/${id}/events`);
      return data;
    },
    enabled: Boolean(id),
    staleTime: 60_000,
  });
}

export function useSessionDpiEvents(id: string) {
  return useQuery({
    queryKey: [...aiSessionKeys.detail(id), 'dpi-events'] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<DpiEvent[]>(`/ai-sessions/${id}/dpi-events`);
      return data;
    },
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useSessionDpiEvent(sessionId: string, eventId: string | null) {
  return useQuery({
    queryKey: [...aiSessionKeys.detail(sessionId), 'dpi-events', eventId] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<DpiEvent>(`/ai-sessions/${sessionId}/dpi-events/${eventId}`);
      return data;
    },
    enabled: Boolean(sessionId) && Boolean(eventId),
    staleTime: 60_000,
  });
}

// ── Session activity tabs (Prompts / Tool Calls) — cursor-paginated ─────────
// `enabled` gates the network call to whichever tab is actually open; once a
// page has been fetched it stays in the react-query cache (staleTime: Infinity)
// so switching tabs back and forth never re-fetches — only "Load more" does.

export function useSessionPrompts(sessionId: string, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: [...aiSessionKeys.detail(sessionId), 'prompts'] as const,
    queryFn: async ({ pageParam }) => {
      const { data } = await apiClient.get<CursorPage<DpiEvent>>(
        `/ai-sessions/${sessionId}/prompts`,
        { params: { cursor: pageParam, limit: ACTIVITY_PAGE_SIZE } },
      );
      return data;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: enabled && Boolean(sessionId),
    staleTime: Infinity,
  });
}

export function useSessionToolCalls(sessionId: string, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: [...aiSessionKeys.detail(sessionId), 'tool-calls'] as const,
    queryFn: async ({ pageParam }) => {
      const { data } = await apiClient.get<CursorPage<DpiEvent>>(
        `/ai-sessions/${sessionId}/tool-calls`,
        { params: { cursor: pageParam, limit: ACTIVITY_PAGE_SIZE } },
      );
      return data;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: enabled && Boolean(sessionId),
    staleTime: Infinity,
  });
}

// Cursor-paginated variant for the endpoint detail page's AI Sessions tab.
export function useAiSessionsByEndpointCursor(endpointId: string, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: [...aiSessionKeys.all, 'cursor', endpointId] as const,
    queryFn: async ({ pageParam }) => {
      const { data } = await apiClient.get<CursorPage<AiSession>>(
        '/ai-sessions/cursor',
        { params: { endpointId, cursor: pageParam, limit: ACTIVITY_PAGE_SIZE } },
      );
      return data;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: enabled && Boolean(endpointId),
    staleTime: Infinity,
  });
}

export function useSessionWorkflow(sessionId: string) {
  return useQuery({
    queryKey: [...aiSessionKeys.detail(sessionId), 'workflow'] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<WorkflowGraph>(
        `/ai-sessions/${sessionId}/workflow`,
      );
      return data;
    },
    enabled: Boolean(sessionId),
    staleTime: 60_000,
  });
}
